import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import toast from '../utils/toast';
import PasteUploadArea from './PasteUploadArea';

// Simplified MasterDataSelector for CustomPageRenderer
const SimpleMasterDataSelector = ({ value, onChange, masterDataList, placeholder }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);

  console.log('SimpleMasterDataSelector props:', { value, masterDataList, placeholder });
  console.log('Master data list length:', masterDataList?.length);
  //console.log('Master data list sample:', masterDataList?.slice(0, 2));
  if (!masterDataList) masterDataList = [];
  console.log('masterDataList', masterDataList);
  const filteredData = masterDataList.filter(item =>
    item.clientName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.clientAddress?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  console.log('Search term:', searchTerm);
  console.log('Filtered data length:', filteredData.length);
  console.log('Filtered data sample:', filteredData.slice(0, 2));

  const handleSelect = (item) => {
    console.log('Selected item:', item);
    onChange(item);
    setShowDropdown(false);
    setSearchTerm('');
  };

  return (
    <div className="relative">
      <input
        type="text"
        placeholder={placeholder || "Search for a client..."}
        value={value ? value.clientName : searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        onFocus={() => {
          console.log('Input focused, showing dropdown');
          setShowDropdown(true);
        }}
        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
      />

      {showDropdown && (
        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
          {filteredData.length === 0 ? (
            <div className="p-4 text-center text-gray-500">
              {masterDataList.length === 0 ? 'No master data available' : 'No clients found'}
            </div>
          ) : (
            <div className="py-1">
              {filteredData.map((item) => (
                <button
                  key={item._id}
                  type="button"
                  onClick={() => handleSelect(item)}
                  className="w-full text-left px-4 py-2 hover:bg-gray-100 focus:outline-none focus:bg-gray-100"
                >
                  <div className="font-medium text-gray-900">{item.clientName}</div>
                  {item.clientAddress && (
                    <div className="text-sm text-gray-600">{item.clientAddress}</div>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Click outside to close dropdown */}
      {showDropdown && (
        <div
          className="fixed inset-0 z-0"
          onClick={() => setShowDropdown(false)}
        />
      )}
    </div>
  );
};

// Simplified UserSelector for CustomPageRenderer
const SimpleUserSelector = ({ value, onChange, userList, placeholder }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);

  const filteredUsers = userList.filter(user =>
    user.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSelect = (user) => {
    onChange(user);
    setShowDropdown(false);
    setSearchTerm('');
  };

  return (
    <div className="relative">
      <input
        type="text"
        placeholder={placeholder || "Search for a user..."}
        value={value ? `${value.firstName} ${value.lastName}` : searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        onFocus={() => setShowDropdown(true)}
        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
      />

      {showDropdown && (
        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
          {filteredUsers.length === 0 ? (
            <div className="p-4 text-center text-gray-500">No users found</div>
          ) : (
            <div className="py-1">
              {filteredUsers.map((user) => (
                <button
                  key={user._id}
                  type="button"
                  onClick={() => handleSelect(user)}
                  className="w-full text-left px-4 py-2 hover:bg-gray-100 focus:outline-none focus:bg-gray-100"
                >
                  <div className="font-medium text-gray-900">{user.firstName} {user.lastName}</div>
                  <div className="text-sm text-gray-600">{user.email}</div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Click outside to close dropdown */}
      {showDropdown && (
        <div
          className="fixed inset-0 z-0"
          onClick={() => setShowDropdown(false)}
        />
      )}
    </div>
  );
};

const CustomPageRenderer = () => {
  const { pageId } = useParams();
  const { user, checkPermission } = useAuth();
  const navigate = useNavigate();

  const [customPage, setCustomPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [masterDataList, setMasterDataList] = useState([]);
  const [userList, setUserList] = useState([]);
  const [documentVerifications, setDocumentVerifications] = useState([]);
  const [documentUploads, setDocumentUploads] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [autoFillSuggestions, setAutoFillSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [searchingSuggestions, setSearchingSuggestions] = useState(false);
  const searchTimeoutRef = useRef(null);
  const [fieldOptions, setFieldOptions] = useState({});
  const [submissionDeadline, setSubmissionDeadline] = useState({ type: '' });
  const [submissionComment, setSubmissionComment] = useState('');
  const [deadlineAutoCalculate, setDeadlineAutoCalculate] = useState({}); // { fieldName: { number: '', unit: '' } }

  const canCreate = checkPermission('Create Custom Page Submission');
  const canUpdate = checkPermission('Update Custom Page Submission');

  // Check if user has access to this page
  const hasPageAccess = () => {
    console.log('hasPageAccess', customPage);
    if (!customPage) return false;

    // Admins have access to all pages
    if (user.role === 'admin') return true;

    // Check if user is in accessUsers
    if (customPage.accessUsers && customPage.accessUsers.length > 0) {
      const userInAccessList = customPage.accessUsers.some(userId => {
        const userIdStr = typeof userId === 'object' ? (userId._id || userId.id) : userId;
        return userIdStr === user.id || userIdStr === user._id;
      });
      if (userInAccessList) return true;
    }

    // Check if user is in accessGroups
    if (customPage.accessGroups && customPage.accessGroups.length > 0) {
      const userInAccessGroup = customPage.accessGroups.some(groupId => {
        const groupIdStr = typeof groupId === 'object' ? (groupId._id || groupId.id) : groupId;
        return user.groupId === groupIdStr || (user.group && user.group._id === groupIdStr);
      });
      if (userInAccessGroup) return true;
    }

    // If both accessUsers and accessGroups are empty, page is NOT accessible to non-admins
    return false;
  };

  useEffect(() => {
    fetchCustomPage();
    fetchMasterData();
    fetchUsers();
  }, [pageId]);

  const fetchDocumentVerifications = async () => {
    try {
      const response = await api.get('/custom-documents');
      if (response.data.success) {
        const docs = response.data.data.docs || response.data.data || [];
        setDocumentVerifications(docs);

        // Get required Checklist for this custom page
        const requiredDocs = getRequiredDocumentVerifications();

        // Initialize document uploads with the actual uploaded files from custom documents
        const initialUploads = {};
        for (const doc of requiredDocs) {
          if (doc.file) {
            // Convert the uploaded document to the format expected by single file structure
            initialUploads[doc._id] = {
              fileName: doc.file.name,
              fileUrl: doc.file.url,
              originalName: doc.file.originalName,
              mimeType: doc.file.mimeType,
              size: doc.file.size
            };
          }
        }
        setDocumentUploads(initialUploads);
      }
    } catch (error) {
      console.error('Error fetching Checklist:', error);
    }
  };

  const fetchCustomPage = async () => {
    try {
      const response = await api.get(`/custom-pages/${pageId}`);
      if (response.data.success) {
        const pageData = response.data.data;
        console.log('Custom page loaded - Auto-fill field:', pageData.autoFillField);
        console.log('Custom page fields:', pageData.fields);
        console.log('Fields with deadlineType:', pageData.fields?.filter(f => f.deadlineType));
        console.log('Fields with enableComments:', pageData.fields?.filter(f => f.enableComments));
        setCustomPage(pageData);
        // Initialize form data with empty values
        const initialData = {};
        pageData.fields.forEach(field => {
          if (field.type === 'dropdown') {
            initialData[field.name] = '';
          } else if (field.type === 'multitext') {
            initialData[field.name] = '';
          } else if (field.type === 'upload') {
            initialData[field.name] = [];
          } else if (field.type === 'masterdata') {
            initialData[field.name] = null;
          } else if (field.type === 'user') {
            initialData[field.name] = null;
          } else {
            initialData[field.name] = '';
          }
        });
        setFormData(initialData);

        // Fetch Checklist after custom page is loaded
        await fetchDocumentVerifications();
      }
    } catch (error) {
      console.error('Error fetching custom page:', error);
      toast.error('Failed to fetch page configuration');
    } finally {
      setLoading(false);
    }
  };

  const fetchMasterData = async () => {
    try {
      const response = await api.get('/master-data');
      console.log('Master data response:', response.data);
      // The master data API returns data directly, not wrapped in success property
      setMasterDataList(response.data.data);
      console.log('Master data list set:', response.data);
    } catch (error) {
      console.error('Error fetching master data:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await api.get('/users');
      if (response.data.success) {
        setUserList(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const searchSubmissionsByFieldValue = async (fieldName, fieldValue) => {
    if (!customPage?.autoFillField) {
      return;
    }

    if (customPage.autoFillField !== fieldName) {
      return;
    }

    // Convert value to string for search
    let searchValue = '';
    if (typeof fieldValue === 'object' && fieldValue !== null) {
      // For masterdata or user objects, use clientName or firstName/lastName
      searchValue = fieldValue.clientName || fieldValue.firstName || fieldValue.email || '';
    } else if (fieldValue !== null && fieldValue !== undefined) {
      searchValue = String(fieldValue).trim();
    }

    if (!searchValue || searchValue.length < 2) {
      setAutoFillSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    try {
      setSearchingSuggestions(true);
      const response = await api.get(`/custom-pages/${pageId}/submissions/search`, {
        params: {
          fieldName: fieldName,
          fieldValue: searchValue
        }
      });

      if (response.data.success) {
        setAutoFillSuggestions(response.data.data);
        setShowSuggestions(response.data.data.length > 0);
        console.log(`Found ${response.data.data.length} suggestions for "${searchValue}"`);
      }
    } catch (error) {
      console.error('Error searching submissions:', error);
      if (error.response) {
        console.error('API Error:', error.response.data);
      }
      setAutoFillSuggestions([]);
      setShowSuggestions(false);
    } finally {
      setSearchingSuggestions(false);
    }
  };

  const handleInputChange = (fieldName, value) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: value
    }));

    // Clear error for this field
    if (errors[fieldName]) {
      setErrors(prev => ({
        ...prev,
        [fieldName]: null
      }));
    }

    // If this is the auto-fill field, search for suggestions with debounce
    if (customPage?.autoFillField === fieldName) {
      // Clear previous timeout
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }

      // Set new timeout for debounced search
      searchTimeoutRef.current = setTimeout(() => {
        searchSubmissionsByFieldValue(fieldName, value);
      }, 500);
    } else {
      // If not auto-fill field, hide suggestions
      setShowSuggestions(false);
    }
  };

  const handleSelectSuggestion = (suggestion) => {
    // Auto-fill only fields that have autoFill enabled
    const fieldsToFill = {};
    const fieldsToClearErrors = [];
    
    if (customPage?.fields) {
      customPage.fields.forEach(field => {
        // Only auto-fill if the field has autoFill enabled
        if (field.autoFill && suggestion.data && suggestion.data[field.name] !== undefined) {
          fieldsToFill[field.name] = suggestion.data[field.name];
          fieldsToClearErrors.push(field.name);
        }
      });
    }

    // Update form data with only auto-fill enabled fields
    setFormData(prev => ({
      ...prev,
      ...fieldsToFill
    }));

    // Clear errors for auto-filled fields
    const clearedErrors = { ...errors };
    fieldsToClearErrors.forEach(fieldName => {
      delete clearedErrors[fieldName];
    });
    setErrors(clearedErrors);

    // Hide suggestions
    setShowSuggestions(false);
    setAutoFillSuggestions([]);

    const filledCount = Object.keys(fieldsToFill).length;
    if (filledCount > 0) {
      toast.success(`${filledCount} field${filledCount > 1 ? 's' : ''} auto-filled from previous submission`);
    } else {
      toast.info('No fields are configured for auto-fill');
    }
  };

  const handleDocumentUpload = (documentId, files) => {
    setDocumentUploads(prev => ({
      ...prev,
      [documentId]: files
    }));
  };

  const uploadFileToServer = async (file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post('/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data.success) {
        return {
          fileName: file.name,
          fileUrl: response.data.url,
          originalName: file.name,
          mimeType: file.type,
          size: file.size
        };
      } else {
        throw new Error('Upload failed');
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      toast.error('Failed to upload file');
      return null;
    }
  };

  const handleFileUpload = async (documentId, files) => {
    try {
      console.log('handleFileUpload called with:', { documentId, files });

      // If no files provided, clear the upload
      if (!files || files.length === 0) {
        console.log('No files provided, clearing document uploads for', documentId);
        handleDocumentUpload(documentId, null);
        return;
      }

      // Take the first file only (since we're using single file structure)
      const file = files[0];
      console.log('Processing file:', file);

      // If the file is already a File object (new upload), upload it
      if (file instanceof File) {
        console.log('Uploading new file to server:', file.name);
        const uploadedFile = await uploadFileToServer(file);
        if (uploadedFile) {
          console.log('File uploaded successfully:', uploadedFile);
          handleDocumentUpload(documentId, uploadedFile);
        }
      } else {
        // If it's already an uploaded file object, keep it as is
        console.log('File already uploaded, keeping as is:', file);
        handleDocumentUpload(documentId, file);
      }
    } catch (error) {
      console.error('Error handling file upload:', error);
      toast.error('Failed to upload files');
    }
  };

  const getRequiredDocumentVerifications = () => {
    if (!customPage || !customPage.documentVerifications || !documentVerifications.length) {
      return [];
    }

    return documentVerifications.filter(doc =>
      customPage.documentVerifications.some(requiredDoc =>
        requiredDoc === doc._id || requiredDoc._id === doc._id
      )
    );
  };

  const validateForm = () => {
    const newErrors = {};

    customPage.fields.forEach(field => {
      if (field.required) {
        const value = formData[field.name];
        if (!value || (Array.isArray(value) && value.length === 0) ||
          (typeof value === 'string' && value.trim() === '')) {
          newErrors[field.name] = `${field.label} is required`;
        }
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrors({});

    try {
      // Validate form data
      const newErrors = {};

      customPage.fields.forEach(field => {
        if (field.required) {
          const value = formData[field.name];
          if (!value || (Array.isArray(value) && value.length === 0) || (typeof value === 'string' && value.trim() === '')) {
            newErrors[field.name] = `${field.label} is required`;
          }
        }
      });

      if (Object.keys(newErrors).length > 0) {
        setErrors(newErrors);
        toast.error('Please fix the errors before submitting');
        return;
      }

      // Process form data
      const processedFormData = { ...formData };

      // Upload files for upload fields
      for (const field of customPage.fields) {
        if (field.type === 'upload' && formData[field.name] && formData[field.name].length > 0) {
          console.log(`Processing upload field: ${field.name}`, formData[field.name]);
          
          const uploadedFiles = [];
          for (const file of formData[field.name]) {
            if (file instanceof File) {
              console.log('Uploading file:', file.name);
              const uploadedFile = await uploadFileToServer(file);
              if (uploadedFile) {
                uploadedFiles.push(uploadedFile);
              }
            } else {
              // File is already uploaded
              uploadedFiles.push(file);
            }
          }
          
          processedFormData[field.name] = uploadedFiles;
        }
      }

      // Prepare field options data (consignmentTracking from field configuration)
      const fieldOptionsData = {};
      customPage.fields.forEach(field => {
        if (field.consignmentTracking) {
          fieldOptionsData[field.name] = {
            consignmentTracking: true
          };
        }
      });

      // Prepare deadline data (single deadline for submission)
      const deadlineData = submissionDeadline.type ? {
        type: submissionDeadline.type
      } : null;

      const submissionData = {
        data: processedFormData,
        status: 'submitted',
        fieldOptions: fieldOptionsData,
        deadline: deadlineData,
        comment: submissionComment || null
      };

      // Add Checklist if any are required
      const requiredDocs = getRequiredDocumentVerifications();
      if (requiredDocs.length > 0) {
        submissionData.documentVerifications = requiredDocs.map(doc => {
          const uploadedFile = documentUploads[doc._id];

          // If file was uploaded, use the uploaded file URL
          // Otherwise, use the custom document's file URL as default
          const fileUrl = uploadedFile
            ? uploadedFile.fileUrl
            : (doc.file ? doc.file.url : null);

          const fileName = uploadedFile
            ? uploadedFile.fileName
            : (doc.file ? doc.file.name : doc.name);

          return {
            documentId: doc._id,
            documentName: doc.name,
            fileUrl: fileUrl,
            fileName: fileName,
            files: uploadedFile ? [uploadedFile] : []
          };
        });
      }

      console.log('Submitting form data:', submissionData);
      const response = await api.post(`/custom-pages/${pageId}/submissions`, submissionData);

      if (response.data.success) {
        toast.success('Form submitted successfully');
        // Redirect to submissions list
        navigate(`/admin/custom-page-submissions/${pageId}`);
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error(error.response?.data?.message || 'Failed to submit form');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Helper function to render field values for suggestions (similar to submissions table)
  const renderSuggestionFieldValue = (field, value) => {
    if (!value) return '-';

    // Handle Map data structure
    if (value instanceof Map) {
      value = Object.fromEntries(value);
    }

    switch (field.type) {
      case 'masterdata':
        if (value && typeof value === 'object') {
          return value.clientName || 'N/A';
        }
        return value || '-';

      case 'user':
        if (value && typeof value === 'object') {
          return `${value.firstName || ''} ${value.lastName || ''}`.trim() || 'N/A';
        }
        return value || '-';

      case 'date':
        if (value) {
          try {
            return new Date(value).toLocaleDateString();
          } catch {
            return value;
          }
        }
        return '-';

      case 'multitext':
        if (typeof value === 'string') {
          return value.length > 50 ? `${value.substring(0, 50)}...` : value;
        }
        return value || '-';

      case 'number':
        return value?.toString() || '-';

      default:
        if (typeof value === 'string') {
          return value.length > 50 ? `${value.substring(0, 50)}...` : value;
        }
        return value?.toString() || '-';
    }
  };

  // Get fields that should be shown in suggestions (same as table)
  const getSuggestionFields = () => {
    if (!customPage?.fields) return [];
    
    return customPage.fields.filter(field => 
      field.showInTable && field.type !== 'upload'
    );
  };

  const renderFieldWithSuggestions = (field, inputElement) => {
    const isAutoFillField = customPage?.autoFillField === field.name;
    
    if (!isAutoFillField) {
      return inputElement;
    }

    const suggestionFields = getSuggestionFields();

    return (
      <div className="relative">
        {inputElement}
        {showSuggestions && autoFillSuggestions.length > 0 && (
          <div className="absolute z-50 left-0 mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-96 overflow-auto" style={{ minWidth: '600px', maxWidth: '90vw' }}>
            {searchingSuggestions && (
              <div className="px-3 py-2 text-sm text-gray-500 text-center">
                Searching...
              </div>
            )}
            {!searchingSuggestions && (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      {suggestionFields.length > 0 ? (
                        suggestionFields.map((tableField) => (
                          <th
                            key={tableField.name}
                            className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap"
                          >
                            {tableField.label}
                          </th>
                        ))
                      ) : (
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                          Value
                        </th>
                      )}
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap">
                        Submitted By
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap">
                        Date
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap">
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {autoFillSuggestions.map((suggestion, index) => {
                      const submittedDate = new Date(suggestion.createdAt).toLocaleDateString();
                      const submittedBy = suggestion.submittedBy 
                        ? `${suggestion.submittedBy.firstName || ''} ${suggestion.submittedBy.lastName || ''}`.trim()
                        : 'Unknown';

                      return (
                        <tr
                          key={suggestion._id || index}
                          onClick={() => handleSelectSuggestion(suggestion)}
                          className="hover:bg-blue-50 cursor-pointer transition-colors"
                        >
                          {suggestionFields.length > 0 ? (
                            suggestionFields.map((tableField) => {
                              const fieldValue = suggestion.data?.[tableField.name];
                              const renderedValue = renderSuggestionFieldValue(tableField, fieldValue);
                              
                              return (
                                <td
                                  key={tableField.name}
                                  className="px-3 py-2 text-sm text-gray-900 whitespace-nowrap"
                                >
                                  {renderedValue}
                                </td>
                              );
                            })
                          ) : (
                            <td className="px-3 py-2 text-sm text-gray-900">
                              {typeof suggestion.matchValue === 'object' 
                                ? (suggestion.matchValue?.clientName || suggestion.matchValue?.firstName || JSON.stringify(suggestion.matchValue))
                                : suggestion.matchValue}
                            </td>
                          )}
                          <td className="px-3 py-2 text-sm text-gray-900 whitespace-nowrap">
                            {submittedBy}
                          </td>
                          <td className="px-3 py-2 text-sm text-gray-900 whitespace-nowrap">
                            {submittedDate}
                          </td>
                          <td className="px-3 py-2 text-sm text-blue-600 font-medium whitespace-nowrap">
                            Click to fill →
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
        {showSuggestions && autoFillSuggestions.length === 0 && !searchingSuggestions && (
          <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg">
            <div className="px-3 py-2 text-sm text-gray-500 text-center">
              No previous submissions found
            </div>
          </div>
        )}
        {/* Click outside to close suggestions */}
        {showSuggestions && (
          <div
            className="fixed inset-0 z-40"
            onClick={() => setShowSuggestions(false)}
          />
        )}
      </div>
    );
  };

  const renderField = (field) => {
    const value = formData[field.name];
    const error = errors[field.name];
    const isAutoFillField = customPage?.autoFillField === field.name;

    switch (field.type) {
      case 'text':
        const textInput = (
          <input
            type="text"
            value={value || ''}
            onChange={(e) => handleInputChange(field.name, e.target.value)}
            onFocus={() => {
              if (isAutoFillField && value && value.trim().length >= 2) {
                searchSubmissionsByFieldValue(field.name, value);
              }
            }}
            placeholder={field.placeholder}
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${error ? 'border-red-500' : 'border-gray-300'
              }`}
          />
        );
        return renderFieldWithSuggestions(field, textInput);

      case 'number':
        const numberInput = (
          <input
            type="number"
            value={value || ''}
            onChange={(e) => handleInputChange(field.name, parseFloat(e.target.value) || '')}
            onFocus={() => {
              if (isAutoFillField && value && value.toString().trim().length >= 2) {
                searchSubmissionsByFieldValue(field.name, value);
              }
            }}
            placeholder={field.placeholder}
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${error ? 'border-red-500' : 'border-gray-300'
              }`}
          />
        );
        return renderFieldWithSuggestions(field, numberInput);

      case 'date':
        const isDeadlineField = field.isDeadlineField;
        const autoCalc = deadlineAutoCalculate[field.name] || { number: '', unit: '' };
        
        const handleAutoCalculate = (number, unit) => {
          if (number && unit) {
            const num = parseFloat(number);
            if (!isNaN(num) && num > 0) {
              const calculatedDate = new Date();
              
              switch (unit) {
                case 'days':
                  calculatedDate.setDate(calculatedDate.getDate() + num);
                  break;
                case 'months':
                  calculatedDate.setMonth(calculatedDate.getMonth() + num);
                  break;
                case 'years':
                  calculatedDate.setFullYear(calculatedDate.getFullYear() + num);
                  break;
                default:
                  return;
              }
              
              // Format date as YYYY-MM-DD for date input
              const year = calculatedDate.getFullYear();
              const month = String(calculatedDate.getMonth() + 1).padStart(2, '0');
              const day = String(calculatedDate.getDate()).padStart(2, '0');
              const formattedDate = `${year}-${month}-${day}`;
              
              handleInputChange(field.name, formattedDate);
            }
          }
        };
        
        const handleAutoCalcNumberChange = (e) => {
          const newNumber = e.target.value;
          setDeadlineAutoCalculate(prev => ({
            ...prev,
            [field.name]: { ...autoCalc, number: newNumber }
          }));
          
          if (newNumber && autoCalc.unit) {
            handleAutoCalculate(newNumber, autoCalc.unit);
          }
        };
        
        const handleAutoCalcUnitChange = (e) => {
          const newUnit = e.target.value;
          setDeadlineAutoCalculate(prev => ({
            ...prev,
            [field.name]: { ...autoCalc, unit: newUnit }
          }));
          
          if (autoCalc.number && newUnit) {
            handleAutoCalculate(autoCalc.number, newUnit);
          }
        };
        
        return (
          <div className="space-y-2">
            {isDeadlineField && (
              <div className="bg-blue-50 p-3 rounded-lg border border-blue-200 mb-3">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Auto Calculate Deadline
                </label>
                <div className="flex items-center space-x-2">
                  <input
                    type="number"
                    min="1"
                    value={autoCalc.number}
                    onChange={handleAutoCalcNumberChange}
                    placeholder="Enter number"
                    className="w-24 px-2 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  />
                  <select
                    value={autoCalc.unit}
                    onChange={handleAutoCalcUnitChange}
                    className="px-2 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  >
                    <option value="">Select unit</option>
                    <option value="days">Day(s)</option>
                    <option value="months">Month(s)</option>
                    <option value="years">Year(s)</option>
                  </select>
                </div>
                {(autoCalc.number && autoCalc.unit) && (
                  <p className="text-xs text-blue-600 mt-2">
                    Date will be set to {parseFloat(autoCalc.number) || 0} {autoCalc.unit} from now
                  </p>
                )}
              </div>
            )}
            <input
              type="date"
              value={value}
              onChange={(e) => {
                handleInputChange(field.name, e.target.value);
                // Clear auto-calculate when manually editing date
                if (isDeadlineField) {
                  setDeadlineAutoCalculate(prev => ({
                    ...prev,
                    [field.name]: { number: '', unit: '' }
                  }));
                }
              }}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${error ? 'border-red-500' : 'border-gray-300'
                }`}
            />
          </div>
        );

      case 'dropdown':
        return (
          <select
            value={value}
            onChange={(e) => handleInputChange(field.name, e.target.value)}
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${error ? 'border-red-500' : 'border-gray-300'
              }`}
          >
            <option value="">Select an option</option>
            {(field.options || []).map((option, index) => (
              <option key={index} value={option}>{option}</option>
            ))}
          </select>
        );

      case 'multitext':
        return (
          <textarea
            value={value}
            onChange={(e) => handleInputChange(field.name, e.target.value)}
            placeholder={field.placeholder}
            rows={4}
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${error ? 'border-red-500' : 'border-gray-300'
              }`}
          />
        );

      case 'upload':
        // Get configured file types and max size, with defaults
        // Normalize acceptedFileTypes to ensure it's an array
        const normalizedTypes = Array.isArray(field.acceptedFileTypes) 
          ? field.acceptedFileTypes 
          : (field.acceptedFileTypes ? String(field.acceptedFileTypes).split(',').map(t => t.trim()) : []);
        
        const acceptedTypes = normalizedTypes.length > 0 
          ? normalizedTypes.join(',') 
          : '*/*'; // Default to all types if none specified
        const maxSize = (field.maxFileSize || 10) * 1024 * 1024; // Convert MB to bytes
        
        return (
          <div className="space-y-2">
            <PasteUploadArea
              files={value}
              onFileSelect={(files) => {
                console.log('File selected in upload field:', files);
                handleInputChange(field.name, files);
              }}
              onFilePaste={(files) => {
                console.log('File pasted in upload field:', files);
                handleInputChange(field.name, files);
              }}
              multiple={true}
              accept={acceptedTypes}
              maxSize={maxSize}
            />
            {field.acceptedFileTypes && field.acceptedFileTypes.length > 0 && (
              <p className="text-xs text-gray-500">
                Allowed types: {field.acceptedFileTypes.join(', ')} | Max size: {field.maxFileSize || 10} MB
              </p>
            )}
          </div>
        );

      case 'masterdata':
        return (
          <SimpleMasterDataSelector
            value={value}
            onChange={(masterData) => handleInputChange(field.name, masterData)}
            masterDataList={masterDataList}
            placeholder={field.placeholder}
          />
        );

      case 'user':
        return (
          <SimpleUserSelector
            value={value}
            onChange={(user) => handleInputChange(field.name, user)}
            userList={userList}
            placeholder={field.placeholder}
          />
        );

      default:
        const defaultInput = (
          <input
            type="text"
            value={value || ''}
            onChange={(e) => handleInputChange(field.name, e.target.value)}
            onFocus={() => {
              const isAutoFillField = customPage?.autoFillField === field.name;
              if (isAutoFillField && value && String(value).trim().length >= 2) {
                searchSubmissionsByFieldValue(field.name, value);
              }
            }}
            placeholder={field.placeholder}
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${error ? 'border-red-500' : 'border-gray-300'
              }`}
          />
        );
        return renderFieldWithSuggestions(field, defaultInput);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!customPage) {
    return (
      <div className="text-center py-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Page Not Found</h2>
        <p className="text-gray-600">The requested page could not be found.</p>
      </div>
    );
  }

  if (!hasPageAccess()) {
    return (
      <div className="text-center py-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Access Denied</h2>
        <p className="text-gray-600">You don't have permission to access this page.</p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">{customPage.name}</h1>
        {customPage.description && (
          <p className="text-gray-600">{customPage.description}</p>
        )}
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        {customPage.fields.map((field, index) => {
          return (
            <div key={index} className="space-y-2 border-b border-gray-200 pb-4">
              <label className="block text-sm font-medium text-gray-700">
                {field.label}
                {field.required && <span className="text-red-500 ml-1">*</span>}
              </label>

              {renderField(field)}

              {errors[field.name] && (
                <p className="text-sm text-red-600">{errors[field.name]}</p>
              )}

              {field.type === 'date' && field.isDeadlineField && (
                <p className="text-sm text-blue-600">
                  This field will be used to calculate task deadlines
                </p>
              )}
            </div>
          );
        })}

        {/* Comments Field - Always available */}
        <div className="space-y-2 pt-4 border-t">
          <label className="block text-sm font-medium text-gray-700">
            Comments
          </label>
          <textarea
            value={submissionComment}
            onChange={(e) => setSubmissionComment(e.target.value)}
            placeholder="Enter your comments..."
            rows={4}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        {/* Deadline Field - Always available */}
        <div className="space-y-2 pt-4 border-t">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Deadline
          </label>
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
            <div className="space-y-3">
              <div className="flex items-center space-x-3">
                <label className="text-sm font-medium text-gray-700 min-w-[100px]">Type:</label>
                <select
                  value={submissionDeadline.type}
                  onChange={(e) => {
                    setSubmissionDeadline(prev => ({
                      ...prev,
                      type: e.target.value
                    }));
                  }}
                  className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                >
                  <option value="">None</option>
                  <option value="personal">Personal</option>
                  <option value="group">Group</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Checklist Section */}
        {getRequiredDocumentVerifications().length > 0 && (
          <div className="space-y-4 pt-6 border-t">
            <h3 className="text-lg font-medium text-gray-900">Required Checklist</h3>
            <p className="text-sm text-gray-600">
              The following documents are required for this submission. If no file is uploaded, the system will use the default file from the custom document:
            </p>

            <div className="space-y-4">
              {getRequiredDocumentVerifications().map((doc) => (
                <div key={doc._id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h4 className="font-medium text-gray-900">{doc.name}</h4>
                      {doc.description && (
                        <p className="text-sm text-gray-600 mt-1">{doc.description}</p>
                      )}
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        Required
                      </span>
                      {documentUploads[doc._id] && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          Custom Upload
                        </span>
                      )}
                      {!documentUploads[doc._id] && doc.file && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                          Using Default
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Show existing file if any */}
                  {documentUploads[doc._id] ? (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border">
                        <div className="flex items-center space-x-3">
                          <div className="flex-shrink-0">
                            <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                              <span className="text-blue-600 text-xs font-medium">📄</span>
                            </div>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-900">{documentUploads[doc._id].fileName}</p>
                            <p className="text-xs text-gray-500">
                              {documentUploads[doc._id].mimeType} • {documentUploads[doc._id].size ? `${(documentUploads[doc._id].size / 1024).toFixed(1)} KB` : 'Unknown size'}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <a
                            href={documentUploads[doc._id].fileUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                          >
                            Download
                          </a>
                          <button
                            type="button"
                            onClick={() => {
                              handleDocumentUpload(doc._id, null);
                            }}
                            className="text-red-600 hover:text-red-800 text-sm"
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                      <div className="text-center pt-2">
                        <input
                          type="file"
                          onChange={(e) => {
                            console.log('Replace File input change event:', e.target.files);
                            const files = Array.from(e.target.files);
                            console.log('Replace File array:', files);
                            handleFileUpload(doc._id, files);
                          }}
                          multiple
                          className="hidden"
                          id={`file-upload-replace-${doc._id}`}
                        />
                        <label
                          htmlFor={`file-upload-replace-${doc._id}`}
                          className="cursor-pointer inline-flex items-center px-3 py-1 border border-gray-300 rounded-md shadow-sm text-xs font-medium text-gray-700 bg-white hover:bg-gray-50"
                        >
                          Replace File
                        </label>
                      </div>
                    </div>
                  ) : (
                    /* Show upload interface when no files exist */
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
                      <div className="text-center">
                        <p className="text-sm text-gray-600 mb-2">
                          Drag and drop files here or click to browse
                        </p>
                        <input
                          type="file"
                          onChange={(e) => {
                            console.log('File input change event:', e.target.files);
                            const files = Array.from(e.target.files);
                            console.log('Files array:', files);
                            handleFileUpload(doc._id, files);
                          }}
                          multiple
                          className="hidden"
                          id={`file-upload-${doc._id}`}
                        />
                        <label
                          htmlFor={`file-upload-${doc._id}`}
                          className="cursor-pointer inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                        >
                          Choose Files
                        </label>
                      </div>
                    </div>
                  )}

                  {doc.category && (
                    <p className="text-xs text-gray-500 mt-2">
                      Category: {doc.category}
                    </p>
                  )}

                  {doc.file && (
                    <p className="text-xs text-blue-600 mt-2">
                      ✓ Default file available: {doc.file.originalName || doc.file.name}
                      {!documentUploads[doc._id] &&
                        " (will be used if no file is uploaded)"}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Submit Button */}
        <div className="flex justify-end pt-6 border-t">
          <button
            type="submit"
            disabled={isSubmitting}
            className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50"
          >
            {isSubmitting ? 'Submitting...' : 'Submit'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CustomPageRenderer; 